import { Box } from '@mui/material'
import { RxCheck, RxCross2 } from 'react-icons/rx'
import { tss } from 'tss-react/mui'
import { Button, ButtonProps } from '~/components/ui/atoms/button/Button'
import { ButtonSize, ButtonVariant } from '~/components/ui/atoms/button/types'
import { LoadingSpinner } from '~/components/ui/atoms/loading-spinner/LoadingSpinner'
import { getContainerDisplay, getLoadingSpinnerDisplay } from './variants/primitives'

export interface LoadingButtonProps extends ButtonProps {
  loading: boolean
  isSuccess?: boolean
  isFailure?: boolean
}

const useStyles = tss
  .withParams<{
    size: ButtonSize
    variant: ButtonVariant
    loading: boolean
    showSuccessOrFailureButton: boolean
  }>()
  .create(({ variant, loading }) => ({
    container: {
      position: 'relative',
      ...getContainerDisplay(variant, loading),
    },
    mainButtonContainer: {
      position: 'relative',
      display: 'inline-grid',
      ...(loading && { visibility: 'hidden' }),
    },
  }))

/**
 * Renders a text button with optional start and end icons with a loading state
 */
export function LoadingButton({
  variant = 'primary',
  size = 'medium',
  loading,
  isSuccess = false,
  isFailure = false,
  children,
  endIcon,
  startIcon,
  disabled,
  onClick,
  ...rest
}: LoadingButtonProps) {
  const showSuccessOrFailureButton = isSuccess || isFailure
  const { css, classes } = useStyles({ size, variant, loading, showSuccessOrFailureButton })
  return (
    <Box className={classes.container}>
      <LoadingSpinner
        buttonSize={size}
        buttonVariant={variant}
        classes={{ root: css(getLoadingSpinnerDisplay(loading)) }}
        thickness={5}
        type="button"
      />
      <Box className={classes.mainButtonContainer}>
        <Button
          classes={{
            button: css(showSuccessOrFailureButton ? { visibility: 'hidden' } : {}),
          }}
          disabled={disabled}
          endIcon={endIcon}
          onClick={onClick}
          size={size}
          startIcon={startIcon}
          variant={variant}
          {...rest}
        >
          {children}
        </Button>
        <Button
          classes={{
            button: css(
              showSuccessOrFailureButton
                ? { position: 'absolute', height: '100%', width: '100%' }
                : { display: 'none' },
            ),
          }}
          onClick={undefined}
          size={size}
          variant={variant}
          {...rest}
        >
          {isSuccess ? <RxCheck size="20px" /> : <RxCross2 size="20px" />}
        </Button>
      </Box>
    </Box>
  )
}
